import React, { useState, useMemo } from 'react';
import { Subscription } from '../types';
import { EditIcon, DeleteIcon, SubscriptionIcon } from './icons';
import { formatCurrency } from '../utils/formatting';
import ConfirmationModal from './ConfirmationModal';

interface SubscriptionsViewProps {
    subscriptions: Subscription[];
    onEdit: (sub: Subscription) => void;
    onDelete: (id: string) => void;
    currency: string;
}

const SubscriptionCard: React.FC<{ sub: Subscription, onEdit: () => void, onDelete: () => void, currency: string }> = ({ sub, onEdit, onDelete, currency }) => {
    
    const billingCycleText = {
        monthly: 'mensual',
        quarterly: 'trimestral',
        yearly: 'anual'
    };
    
    const nextBillingDate = new Date(sub.nextBillingDate);
    
    const cancellationDate = sub.cancellationDate !== 'never' ? new Date(sub.cancellationDate) : null;

    return (
        <div className={`bg-light-surface dark:bg-surface p-5 rounded-lg border border-light-border dark:border-border shadow-md flex flex-col justify-between ${sub.status === 'canceled' ? 'opacity-60' : ''}`}>
            <div>
                <div className="flex justify-between items-start">
                    <div>
                        <h4 className="font-bold text-lg text-light-text-primary dark:text-text-primary">{sub.name}</h4>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-light-text-secondary dark:text-text-secondary">{sub.category}</span>
                    </div>
                    <div className="flex gap-1">
                        <button onClick={onEdit} className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-primary transition-colors" title="Editar"><EditIcon className="w-5 h-5"/></button>
                        <button onClick={onDelete} className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-red-500 transition-colors" title="Eliminar"><DeleteIcon className="w-5 h-5"/></button>
                    </div>
                </div>

                <div className="my-4">
                    <p className="text-3xl font-bold text-red-500">{formatCurrency(sub.amount, currency)}</p>
                     <p className="text-sm text-light-text-secondary dark:text-text-secondary">
                        Cobro {billingCycleText[sub.billingCycle]}
                    </p>
                </div>

                <div>
                    {sub.status === 'active' && (
                         <p className="text-sm text-light-text-secondary dark:text-text-secondary">
                            Próximo cobro: <span className="font-semibold text-light-text-primary dark:text-text-primary">{nextBillingDate.toLocaleDateString('es-ES')}</span>
                        </p>
                    )}
                    {cancellationDate && (
                         <p className="text-sm text-light-text-secondary dark:text-text-secondary">
                            Cancelación programada: <span className="font-semibold text-red-500">{cancellationDate.toLocaleDateString('es-ES')}</span>
                        </p>
                    )}
                </div>

            </div>
            {sub.status === 'canceled' && (
                <div className="mt-4 pt-4 border-t border-light-border dark:border-border text-center text-red-500 font-semibold">
                    Cancelada
                </div>
            )}
        </div>
    );
};


const SubscriptionsView: React.FC<SubscriptionsViewProps> = ({ subscriptions, onEdit, onDelete, currency }) => {
    const [filter, setFilter] = useState<'active' | 'canceled'>('active');
    const [deletingSub, setDeletingSub] = useState<Subscription | null>(null);

    const filteredSubs = useMemo(() => {
        return subscriptions
            .filter(sub => sub.status === filter)
            .sort((a, b) => new Date(a.nextBillingDate).getTime() - new Date(b.nextBillingDate).getTime());
    }, [subscriptions, filter]);

    const handleConfirmDelete = () => {
        if (deletingSub) {
            onDelete(deletingSub.id);
            setDeletingSub(null);
        }
    };

    return (
        <>
            <ConfirmationModal
                isOpen={!!deletingSub}
                onClose={() => setDeletingSub(null)}
                onConfirm={handleConfirmDelete}
                title="Confirmar Eliminación"
                message={`¿Estás seguro de que quieres eliminar la suscripción "${deletingSub?.name}"? Esta acción no se puede deshacer.`}
                confirmText="Eliminar"
            />
            <div className="space-y-6">
                <div className="bg-light-surface dark:bg-surface p-4 sm:p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                        <div>
                            <h3 className="font-bold text-xl text-light-text-primary dark:text-text-primary">Gestión de Suscripciones</h3>
                            <p className="text-sm text-light-text-secondary dark:text-text-secondary mt-1">Lleva un control de tus servicios y pagos recurrentes.</p>
                        </div>
                        <div className="flex gap-2 rounded-lg bg-gray-200 dark:bg-gray-800 p-1 mt-4 sm:mt-0">
                            <button onClick={() => setFilter('active')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === 'active' ? 'bg-primary text-white shadow' : 'text-light-text-secondary dark:text-text-secondary'}`}>
                                Activas
                            </button>
                            <button onClick={() => setFilter('canceled')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === 'canceled' ? 'bg-primary text-white shadow' : 'text-light-text-secondary dark:text-text-secondary'}`}>
                                Canceladas
                            </button>
                        </div>
                    </div>
                </div>

                {filteredSubs.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredSubs.map(sub => (
                            <SubscriptionCard 
                                key={sub.id} 
                                sub={sub} 
                                onEdit={() => onEdit(sub)} 
                                onDelete={() => setDeletingSub(sub)} 
                                currency={currency}
                            />
                        ))}
                    </div>
                ) : (
                     <div className="text-center py-16 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border">
                        <SubscriptionIcon className="w-16 h-16 text-light-text-secondary dark:text-text-secondary mx-auto mb-4"/>
                        <h4 className="font-bold text-lg text-light-text-primary dark:text-text-primary">No hay suscripciones {filter === 'active' ? 'activas' : 'canceladas'}.</h4>
                        <p className="text-sm text-light-text-secondary dark:text-text-secondary mt-1">
                            {filter === 'active' 
                                ? 'Añade una nueva suscripción para empezar.' 
                                : 'Aquí aparecerán las suscripciones que hayas cancelado.'}
                        </p>
                    </div>
                )}
            </div>
        </>
    );
};

export default SubscriptionsView;